<script lang="ts" setup>
import { computed, ref } from 'vue';

import { useChatStore } from '#layers/ai/stores/chat';
import StructuredPreview from './ContentBlock/StructuredPreview.vue';

const chatStore = useChatStore();

const request = computed(() => chatStore.pendingApproval);
const isApprovalRequired = computed(() => chatStore.status === 'approvalRequired');
const alternativeReason = ref('');
const trimmedAlternativeReason = computed(() => alternativeReason.value.trim());
const canDenyAndAskForAlternative = computed(() => isApprovalRequired.value && trimmedAlternativeReason.value.length > 0);

async function approveToolApproval() {
	if (!isApprovalRequired.value) return;

	await chatStore.approveToolApproval();
}

async function denyToolApproval() {
	if (!isApprovalRequired.value) return;

	await chatStore.denyToolApproval();
}

async function denyAndAskForAlternative() {
	if (!canDenyAndAskForAlternative.value) return;

	await chatStore.denyToolApproval(trimmedAlternativeReason.value);
	alternativeReason.value = '';
}
</script>

<template>
	<section
		v-if="request"
		data-testid="ai-chat-approval"
		class="mb-2 overflow-hidden rounded-md border border-muted bg-muted/30 text-sm"
		:aria-labelledby="`approval-${request.toolCallId}`"
	>
		<div class="border-b border-muted/70 px-3 py-2">
			<div class="min-w-0">
				<h2 :id="`approval-${request.toolCallId}`" class="text-sm font-medium text-default">
					{{ $t('ai.chat.approval.title') }}
				</h2>

				<p class="mt-0.5 text-xs break-all text-muted">
					{{ request.toolExecute.intent }}
					(<span class="font-mono">{{ request.toolExecute.name }}</span>)
				</p>
			</div>
		</div>

		<div>
			<DAccordion
				:items="[{
					label: $t('ai.chat.tool_input'),
					value: 'input',
				}]"
				type="single"
				collapsible
				class="border-b border-muted/70"
				:ui="{
					item: 'border-none',
					trigger: 'h-8 px-3 text-xs',
					content: 'overflow-hidden',
					leadingIcon: 'size-4 text-muted',
					label: 'text-xs font-medium text-muted',
					body: 'pb-0',
				}"
			>
				<template #body>
					<StructuredPreview v-if="request" :value="request.toolExecute.input" />
				</template>
			</DAccordion>

			<div class="border-b border-muted/70 p-3">
				<div class="grid grid-cols-[2fr_1fr] gap-2">
					<DButton
						type="button"
						data-testid="ai-chat-approve-approval"
						color="neutral"
						variant="outline"
						size="sm"
						icon="lucide:check"
						class="justify-center"
						:label="$t('ai.chat.approval.approve_run')"
						:disabled="!isApprovalRequired"
						@click="approveToolApproval"
					/>

					<DButton
						type="button"
						data-testid="ai-chat-deny-approval"
						color="error"
						variant="ghost"
						size="sm"
						icon="lucide:x"
						class="justify-center"
						:label="$t('ai.chat.approval.deny')"
						:disabled="!isApprovalRequired"
						@click="denyToolApproval"
					/>
				</div>
			</div>

			<form
				data-testid="ai-chat-deny-alternative-form"
				class="w-full px-3 py-2"
				@submit.prevent="denyAndAskForAlternative"
			>
				<DInput
					v-model="alternativeReason"
					data-testid="ai-chat-deny-alternative-reason"
					size="sm"
					block
					:placeholder="$t('ai.chat.approval.deny_alternative_placeholder')"
					:disabled="!isApprovalRequired"
					:ui="{ trailing: 'pe-1' }"
				>
					<template #trailing>
						<DButton
							type="submit"
							data-testid="ai-chat-deny-alternative"
							size="sm"
							color="neutral"
							variant="ghost"
							square
							icon="lucide:send-horizontal"
							:aria-label="$t('ai.chat.approval.deny_alternative')"
							:title="$t('ai.chat.approval.deny_alternative')"
							:disabled="!canDenyAndAskForAlternative"
						/>
					</template>
				</DInput>
			</form>
		</div>
	</section>
</template>
